// @ts-nocheck
/**
 * Shared table toolbar spacing tokens and styles.
 *
 * Every toolbar control (SearchTextField, Autocomplete dropdown,
 * Filter / Manage Columns / Download buttons) should use these
 * values so the UI stays consistent.
 *
 * Spacing standards
 * -----------------
 * TOOLBAR_ICON_LEFT_PADDING : 12px  – left internal padding before icon
 * TOOLBAR_ICON_TEXT_GAP     :  8px  – gap between icon and text/placeholder
 * TOOLBAR_CONTROL_HEIGHT    : 36px  – uniform control height
 */

// ── tokens ──────────────────────────────────────────────────────────────
export const TOOLBAR_ICON_LEFT_PADDING = '12px';
export const TOOLBAR_ICON_TEXT_GAP = '8px';
export const TOOLBAR_CONTROL_HEIGHT = 36; // px (number for sx / style)
export const TOOLBAR_OVERLAP_BREAKPOINT = 1060;

// ── Search TextField sx override ────────────────────────────────────────
// Apply via: <SearchTextfield sx={TOOLBAR_SEARCH_SX} … />
export const TOOLBAR_SEARCH_SX = {
  '& .MuiInputBase-root': {
    height: TOOLBAR_CONTROL_HEIGHT,
    paddingLeft: TOOLBAR_ICON_LEFT_PADDING,
    gap: TOOLBAR_ICON_TEXT_GAP,
  },
  '& input.MuiInputBase-input': {
    padding: '0 !important',
  },
  // Remove extra adornment margin so the gap token controls spacing
  '& .MuiInputAdornment-root': {
    marginRight: 0,
  },
};

// ── Autocomplete sx override ────────────────────────────────────────────
// Apply via: <Autocomplete sx={{ width: 200, ...TOOLBAR_AUTOCOMPLETE_SX }} … />
export const TOOLBAR_AUTOCOMPLETE_SX = {
  '& .MuiInputBase-root': {
    height: 40,
    paddingLeft: `${TOOLBAR_ICON_LEFT_PADDING} !important`,
    gap: TOOLBAR_ICON_TEXT_GAP,
  },
  '& .MuiAutocomplete-input': {
    padding: '0 !important',
  },
  '& .MuiInputAdornment-root': {
    marginRight: 0,
  },
  '& .MuiOutlinedInput-root': {
    borderColor: (theme) =>
      `${theme?.palette?.tableWrapper?.borderColor} !important`,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: (theme) =>
      `${theme?.palette?.tableWrapper?.borderColor} !important`,
  },
};

// ── Autocomplete textfield input sx override ──────────────────────────
export const TOOLBAR_AUTOCOMPLETE_TEXTFIELD_SX = {
  wordBreak: 'break-word',
  whiteSpace: 'wrap',
  marginTop: '0px !important',
  '& .MuiOutlinedInput-root': {
    borderColor: (theme) =>
      `${theme?.palette?.tableWrapper?.borderColor} !important`,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: (theme) =>
      `${theme?.palette?.tableWrapper?.borderColor} !important`,
  },
};

// ── Filter / action button sx override ──────────────────────────────────
// Apply via: <Button sx={{ ...TOOLBAR_BUTTON_SX, … }} … />
export const TOOLBAR_BUTTON_SX = {
  height: `${TOOLBAR_CONTROL_HEIGHT}px !important`,
  paddingLeft: TOOLBAR_ICON_LEFT_PADDING,
  gap: TOOLBAR_ICON_TEXT_GAP,
  display: 'inline-flex',
  alignItems: 'center',
  // Normalise MUI's default startIcon margin so the gap token is the
  // only horizontal space between icon and label.
  '& .MuiButton-startIcon': {
    marginRight: 0,
    marginLeft: 0,
  },
  borderColor: (theme) => theme?.palette?.tableWrapper?.borderColor,
};

// ── Download icon-button sx override ────────────────────────────────────
export const TOOLBAR_ICON_BUTTON_SX = {
  height: 36,
  width: 40,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&.outlined': {
    borderColor: (theme) =>
      `${theme?.palette?.tableWrapper?.borderColor} !important`,
  },
};

// ── Table Container Box sx override ─────────────────────────────────────
export const TABLE_CONTAINER_BOX_SX = {
  '& .MuiTablePagination-selectLabel': { margin: 0 },
  '& .MuiTablePagination-input': { marginLeft: 0 },
  // Override Manage Columns button border from library
  '& .MuiToolbar-root .MuiButton-outlined': {
    borderColor: (theme) => theme?.palette?.tableWrapper?.borderColor,
  },
};

// ── Download button wrapper sx ──────────────────────────────────────────
// The <Box> that wraps the download IconButton above the table.
// Matches the 58px height of the table's internal toolbar to ensure
// perfect vertical centering with 'Manage Columns'.
export const TOOLBAR_DOWNLOAD_WRAPPER_SX = {
  float: 'right',
  display: 'flex',
  alignItems: 'center',
  height: '58px',
  paddingRight: '8px',
};

// ── Toolbar row container sx ────────────────────────────────────────────
// The outer <Box> that holds SearchTextField + Autocomplete + FilterButton …
// Matches the 58px height of the table's internal toolbar so left-aligned
// controls perfectly center with right-aligned controls.

// Remove negative margin when viewport becomes too narrow,
// otherwise the search/filter toolbar overlaps with the
// table toolbar (Manage Columns / Download actions).
export const TOOLBAR_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1, // 8px (MUI spacing unit)
  height: '58px',
  marginBottom: '-58px',
  zIndex: 10,
  width: 'fit-content',
  position: 'relative',
  pl: '8px',

  [`@media (max-width:${TOOLBAR_OVERLAP_BREAKPOINT}px)`]: {
    marginBottom: 0,
    width: '100%',
  },
};
